/**
 * Hook: GET /api/t/{slug}/ranking-runs/by-resource?jd_id=<id> — A30b-2.
 *
 * Returns the latest ``ranking_runs`` row for a JD, regardless of status —
 * the page-load reconciliation surface for the Run-Ranking button binding
 * (spec L4179): ``isInFlightForResource`` is derived from ``status ∈
 * {queued, running}`` here, not from the mutation's ``isPending``.
 *
 * Returns ``null`` data on 404 (no run for this JD) — empty state, not
 * error. While status is queued/running, polls every 3s.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import { useInvalidateJournalOnComplete } from "@/hooks/queries/journal/useInvalidateJournalOnComplete";
import type { FitOverall } from "@/lib/fitScore";
import type { TenureSignal } from "./useRanking";

type RunStatus = "queued" | "running" | "complete" | "failed";

interface RankingRunSnapshot {
  run_id: string;
  status: RunStatus;
  jd_id: string;
  model_used: string | null;
  rankings:
    | Array<{
        candidate_id: string;
        name: string;
        rank_position: number;
        summary: string;
        tenure_signal: TenureSignal;
        fit_overall: FitOverall;
        /** ranking.system v3: stored 0–100 score; null for legacy rows. */
        fit_score: number | null;
      }>
    | null;
  error: string | null;
  retry_count: number | null;
  next_retry_at: string | null;
}

interface UseRankingRunByResourceOpts {
  jdId: string | undefined;
}

export function useRankingRunByResource(
  opts: UseRankingRunByResourceOpts,
): UseQueryResult<RankingRunSnapshot | null, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { jdId } = opts;

  const query = useQuery<RankingRunSnapshot | null, ApiError>({
    queryKey: ["ranking-run-by-resource", tenantSlug, jdId],
    queryFn: async () => {
      try {
        const r = await apiClient.get<RankingRunSnapshot>(
          `/api/t/${tenantSlug}/ranking-runs/by-resource`,
          { params: { jd_id: jdId } },
        );
        return r.data;
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: !!tenantSlug && !!jdId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === "queued" || data.status === "running")) {
        return 3000;
      }
      return false;
    },
  });

  // Card 2 §7 — surface a freshly-completed ranking in the Actions Log without
  // requiring navigation.
  useInvalidateJournalOnComplete(query.data?.status);

  return query;
}
